import { MdlDialogService, MdlTextFieldComponent } from '@angular-mdl/core';
import { MdlPopoverComponent } from '@angular-mdl/popover';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { filter, find, includes, isEmpty, some } from 'lodash';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { NoneValueHelper, Settings } from '../core/';

@Component({
    selector: 'covance-select',
    template: require('./covance-select.component.html'),
    styles: [require('./covance-select.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CovanceSelectComponent implements OnChanges, OnDestroy {

    @ViewChild('selectPopover')
    public selectPopover: MdlPopoverComponent;

    @ViewChild('selectPopoverSearch')
    public selectPopoverSearch: MdlTextFieldComponent;

    @ViewChild('selectEl')
    public selectEl: ElementRef<HTMLDivElement>;

    @Input()
    public containerLeftLimit: number;

    @Input()
    public valueChangeConfirmationText: string = null;

    @Input()
    public loading = false;

    @Input()
    public multiselect = false;

    @Input()
    public allowSearch = true;

    @Input()
    public allItems: any[];

    @Input()
    public sortItems = true;

    @Input()
    public selectedItems: any[];

    @Input()
    public label = '';

    @Input()
    public selectStyle: {
        [key: string]: string;
    };

    @Input()
    public textSelector: (item: any) => string = null;

    @Input()
    public searchInputStyle: {
        [key: string]: string;
    };

    @Input()
    @HostBinding('class.disabled')
    public disabled = false;

    @Output()
    public selectedItemsChanged = new EventEmitter<any[]>();

    @Output()
    public searchTermChanged = new EventEmitter<string>();

    public searchTerm = new FormControl('');

    public resultString = '';

    public options: Option[];

    public filteredOptions: Option[];

    public get isEmptyOptions() {
        return isEmpty(this.options);
    }

    public isAllSelected: boolean;

    public none = NoneValueHelper.none;

    private subscription = new Subscription();

    private readonly popoverWidth = 300; // must be equal to corresponding value in the css file

    constructor(
        changeDetectorRef: ChangeDetectorRef,
        private noneValueHelper: NoneValueHelper,
        private dialogService: MdlDialogService,
        private readonly settings: Settings
    ) {

        const searchTermFlow = this.searchTerm.valueChanges
            .pipe(
                distinctUntilChanged()
            );

        this.subscription.add(
            searchTermFlow.subscribe((term: string) => {
                this.onSearchTermChange(term);
                this.searchTermChanged.emit(term);
                changeDetectorRef.markForCheck();
            })
        );
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (this.allItems) {
            this.options = [];
            let emptyOptionAdded = false;

            for (let option of this.allItems) {
                const isNone = this.noneValueHelper.isNone(option);
                const text = option ?
                                    this.createOptionText(option)
                                    : null;
                const item: Option = {
                    isChecked: some(this.selectedItems, x => x === option || (this.noneValueHelper.isNone(x) && isNone)),
                    isNone: isNone,
                    text: this.noneValueHelper.toDisplay(text),
                    item: option
                };

                if (isNone) {
                    if (!emptyOptionAdded) {
                        emptyOptionAdded = true;
                        this.options.push(item);
                    }
                    continue;
                }

                this.options.push(item);
            }

            if (!this.allowSearch) {
                this.updateResultString();
            }
        }
        
        this.onSearchTermChange(this.searchTerm.value);

        if (changes.selectedItems) {
            this.updateResultString();
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public trackByFn(option: Option) {
        return option.text;
    }

    public async selectAllOnChange(value: boolean) {
        const confirmed = await this.promptForValueChangeConfirmation();
        if (!confirmed) {
            return;
        }

        this.isAllSelected = value;
    }

    public async onMultiSelectOptionChange(option: Option) {
        const confirmed = await this.promptForValueChangeConfirmation();
        if (!confirmed) {
            return;
        }

        option.isChecked = !option.isChecked;
        this.updateSelectedItems();
    }

    public async onSingleSelectOptionChange(option: Option) {
        const isChecked = option.isChecked;
        if (isChecked) {
            this.selectPopover.hide();
            return;
        }

        const confirmed = await this.promptForValueChangeConfirmation();
        if (!confirmed) {
            return;
        }

        this.options.forEach(opt => opt.isChecked = false);
        option.isChecked = !isChecked;
        this.updateSelectedItems();
        this.selectPopover.hide();
    }

    public async selectAllOnClick() {
        const confirmed = await this.promptForValueChangeConfirmation();
        if (!confirmed) {
            return;
        }

        if (this.filteredOptions) {
            const state = this.getSelectAllCheckboxState();
            const isChecked = state === CheckboxState.Unchecked;

            this.filteredOptions.forEach(option => option.isChecked = isChecked);
            this.updateSelectedItems();
        }
    }

    public togglePopover(event: Event) {
        if (!this.disabled) {
            this.selectPopover.position = (this.containerLeftLimit && this.containerLeftLimit - this.popoverWidth < this.selectEl.nativeElement.offsetLeft)
                ? 'bottom-right'
                : 'bottom-left';
            this.selectPopover.toggle(event, this.selectEl.nativeElement);
            if (this.allowSearch && this.selectPopover.isVisible) {
                setTimeout(() => this.selectPopoverSearch.inputEl.nativeElement.focus());
            }
        }
    }

    public onPopoverHide() {
        this.clearSearchTerm();
    }

    public clearSearchTerm() {
        this.searchTerm.setValue(null);
    }

    public clearSelectedItems() {
        this.clearSearchTerm();
        this.options = [];
        this.onSearchTermChange(null);
        this.updateSelectedItems();
    }

    public getSelectAllCheckboxIcon() {
        const state = this.getSelectAllCheckboxState();
        switch (state) {
            case CheckboxState.Checked:
                return 'check_box';
            case CheckboxState.Indeterminate:
                return 'indeterminate_check_box';
            default:
                return 'check_box_outline_blank';
        }
    }

    public getCheckboxIcon(option: Option) {
        return option.isChecked ? 'check_box' : 'check_box_outline_blank';
    }

    private createOptionText(option: any) {
        if (this.textSelector) {
            return this.textSelector(option);
        }

        if (option.value === '') {
            return '';
        }
        return option.value || option.toString();
    }

    private updateSelectedItems() {
        this.selectedItems = this.options.filter(op => op.isChecked).map(op => op.item);
        this.selectedItemsChanged.emit(this.selectedItems);
        this.updateResultString();        
    }

    private getSelectAllCheckboxState() {
        if (this.filteredOptions) {
            const hasChecked = !!find(this.filteredOptions, option => option.isChecked);
            const hasUnchecked = !!find(this.filteredOptions, option => !option.isChecked);

            if (hasChecked) {
                return hasUnchecked ? CheckboxState.Indeterminate : CheckboxState.Checked;
            }
        }

        return CheckboxState.Unchecked;
    }

    private onSearchTermChange(term: string) {
        if (isEmpty(this.options)) {
            this.filteredOptions = [];
            return;
        }

        const isSearchOnly = (this.options.length > this.settings.maxNumberOfVisibleFilterOptions);
        const searchTermIsTooShort = !term || (isSearchOnly && term.length < this.settings.minFilterSearchTermLengthForLargeArrays);

        if (searchTermIsTooShort) {
            this.filteredOptions = isSearchOnly ? this.options.filter(i => i.isChecked) : this.options;
        } else {
            const searchText = term.toLowerCase().trim();
            this.filteredOptions = this.options.filter(i => {
                if (isEmpty(i)) {
                    return false;
                }

                return i.isChecked || (i.text && includes(i.text.toString().toLowerCase(), searchText));
            });
        }

        if (this.filteredOptions) {
            this.filteredOptions.sort((a, b) => {
                if (a.isChecked && !b.isChecked) {
                    return -1;
                }

                if (!a.isChecked && b.isChecked) {
                    return 1;
                }

                return this.sortItems ? this.noneValueHelper.compare(a.text, b.text) : 0;
            });
        }
    }

    private updateResultString() {
        this.resultString = filter(this.options, option => option.isChecked)
            .map(option => this.noneValueHelper.toDisplay(option.text))
            .join(', ');
    }

    private async promptForValueChangeConfirmation() {
        if (this.valueChangeConfirmationText == null) {
            return true;
        }

        try {
            await this.dialogService.confirm(this.valueChangeConfirmationText, 'Cancel', 'OK').toPromise();
            return true;
        } catch (e) {
            return false;
        }
    }
}

type Option = {
    isChecked: boolean;
    isNone: boolean;
    text: string;
    item: any;
};

enum CheckboxState {
    Unchecked,
    Indeterminate,
    Checked
}